import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosRequestConfig } from "axios";
import {
  construct_headers,
  format_url,
  get_token,
} from "../../services/ApiHelper";
import { StatusCode } from "../../services/globals";
import {
  apis,
  get_auth_token_url,
  send_cr_request_url,
} from "../../services/API";

interface InitialState {
  app_dimensions: { desktop: any; mobile: any };
  app_loading: false;
  app_token: any;
  cancel_process: true;
  club: any;
  user: any;
  agreements: any,
  temp_token: any
}

const initialState = {
  app_dimensions: { desktop: true, mobile: false },
  app_loading: false,
  app_token: {},
  cancel_process: true,
  club: {},
  user: {},
  agreements: [],
  temp_token: ""
} as InitialState;

const AppReducer = createSlice({
  name: "app-reducer",
  initialState,
  reducers: {
    update_state_value(state: any, action: PayloadAction<any>) {
      state[action.payload.key] = action.payload.value;
    },
    update_user(state: any, action: PayloadAction<any>) {
      state.user[action.payload.key] = action.payload.value;
    },
    clear_state(state: any) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder: any) => {
    builder
      .addCase(get_api.fulfilled, (state: any, action: any) => {
        state[action.payload.key] = action.payload.value;
        state.loading_status = StatusCode.SUCCESS;
      })
      .addCase(get_api.pending, (state: any) => {
        state.loading_status = StatusCode.LOADING;
      })
      .addCase(get_api.rejected, (state: any) => {
        state.loading_status = StatusCode.ERROR;
      })
      .addCase(post_api.fulfilled, (state: any, action: any) => {
        if (action.payload.key != null)
          state[action.payload.key] = action.payload.value;
        state.loading_status = StatusCode.SUCCESS;
      })
      .addCase(post_api.pending, (state: any) => {
        state.loading_status = StatusCode.LOADING;
      })
      .addCase(post_api.rejected, (state: any, action: any) => {
        state.loading_status = StatusCode.ERROR;
        state.api_results = action.payload;
      })
      .addCase(generate_api_key.fulfilled, (state: any, action: any) => {
        state.api_token = action.payload;
      })
      .addCase(send_cr_request.fulfilled, (state: any, action: any) => {
        let value = action.payload.value.data;
        try {
          value = JSON.parse(JSON.stringify(value));
        } catch (exception) {
          // console.dir(exception);
        }

        state.api_token = value.k;
        delete value.k;
        state[action.payload.key] =
          value.data !== undefined ? value.data : value;
      });
  },
});

export const { update_user, update_state_value, clear_state } =
  AppReducer.actions;

export default AppReducer.reducer;

export const generate_api_key = createAsyncThunk(
  "generate_api_key/get",
  async () => {
    return await axios
      .get(get_auth_token_url)
      .then((results) => results.data.k);
  }
);

export const send_cr_request = createAsyncThunk(
  "send_cr_request/post",
  async (post_data: any, { getState }) => {
    let state: any = getState();
    state = state.app;
    post_data["k"] = state.api_token;
    const state_key = post_data["state_key"];
    delete post_data["state_key"];

    if (state.api_token) {
      return await axios
        .post(send_cr_request_url, post_data)
        .then((response) => {
          return { value: response, key: state_key };
        })
        .catch((error) => {
          return { key: "api_results", value: error.response.data };
        });
    }
  }
);

export const get_api = createAsyncThunk(
  "app/get",
  async (get_data: any, { getState }) => {
    const state: any = getState();
    let url = "";
    let type = "";

    const url_info = apis[get_data["api"]];

    if (url_info["type"] == undefined) {
      url = url_info[get_data["type"]]["url"];
      type = get_data["type"];
    } else {
      url = url_info["url"];
      type = url_info["type"];
    }

    url = format_url(url, get_data["field_values"]);

    if (type == "new") {
      return await get_token().then(async (token: string) => {
        var headers: any = construct_headers("new", token);
        const config: AxiosRequestConfig = {
          method: "get",
          url: url,
          headers: headers,
        };
        return await axios(config)
          .then((response) => {
            const _response =
              get_data["response_key"] != undefined
                ? response.data[get_data["response_key"]]
                : response.data;
            return { value: _response, key: get_data["state_key"] };
          })
          .catch(function (error) {
            return { key: "api_results", value: error.response.data };
          });
      });
    } else {
      var headers: any = construct_headers(type);
      return await axios({
        url,
        method: "get",
        headers: headers,
      })
        .then((response) => {
          const _response =
            get_data["response_key"] != undefined
              ? response.data[get_data["response_key"]]
              : response.data;
          return { value: _response, key: get_data["state_key"] };
        })
        .catch(function (error) {
          return { key: "api_results", value: error.response.data };
        });
    }
  }
);

export const post_api = createAsyncThunk(
  "app/post",
  async (post_data: any, { getState }) => {
    const state: any = getState();

    const url_info = apis[post_data["api"]];

    let url = url_info["url"];
    const type = url_info["type"];
    const state_key =
      post_data["state_key"] == undefined ? null : post_data["state_key"];

    url = format_url(url, post_data["field_values"]);

    if (type == "new") {
      return await get_token().then(async (token: string) => {
        const headers: any = construct_headers(type, token);
        return await axios
          .post(url, post_data["post_data"], { headers })
          .then((response) => {
            return { value: response.data, key: state_key };
          })
          .catch((error) => {
            return { key: "api_results", value: error.response.data };
          });
      });
    } else {
      const headers: any = construct_headers(type);
      return await axios
        .post(url, post_data["post_data"], { headers })
        .then((response) => {
          return { value: response.data, key: state_key };
        })
        .catch(function (error) {
          return { key: "api_results", value: error.response.data };
        });
    }
  }
);